/* eslint-disable import/no-unresolved */
import React, { useEffect, useMemo, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { goBack, push, replace } from "react-router-redux";

import { resizableRoute } from "pages/_components/Resizable";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";

import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import { actions as templateActions, selectors as templateSelectors } from "reducers/template";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as layoutActions } from "reducers/layout";
import { selectors as statusSelectors } from "reducers/status";

import * as i18n from "util/i18n";
import { isUxDashboardImprovementsEnabled, resolveEmbeddedV3GoBackPath } from "util/v3ShellLayoutSync";
import { getTransferHistoricPath } from "util/v3UxConfig";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";

import { Box, Button, Col, Container, Grid, Tab, Tabs, Text } from "@ui-kit/components/index.es";
import TransferInternalForm, {
    getTransferInternalTitleI18nKey,
    getTransferInternalTitleLabel,
} from "./TransferInternalForm";
import TransferThirdPartiesForm from "./TransferThirdPartiesForm";
import TransferToContactForm from "./TransferToContactForm";

const MAX_REQUIRED = "22";
const TRANSFER_GROUPS = {
    Internal: "internal",
    ThirdParties: "thirdParties",
    ToContact: "toContact",
};

function V2Transfer(props) {
    V2Transfer.propTypes = {
        dispatch: func.isRequired,
        transferGroup: string.isRequired,
        location: shape({
            pathname: string.isRequired,
        }).isRequired,
        isDesktop: bool.isRequired,
        activeEnvironment: shape({
            permissions: shape({
                transferInternal: bool,
                transferThirdParties: bool,
            }),
        }).isRequired,
        match: shape({
            params: shape({ idForm: string }),
        }),
        embeddedInV3Shell: bool,
        summary: shape({}),
        lastHrefPathname: string,
    };

    V2Transfer.defaultProps = {
        match: null,
        embeddedInV3Shell: false,
        summary: null,
        lastHrefPathname: "",
    };

    const {
        dispatch,
        match,
        transferGroup,
        isDesktop,
        location,
        activeEnvironment,
        embeddedInV3Shell,
        summary,
        lastHrefPathname,
    } = props;

    const [agendaVisible, setAgendaVisible] = useState(true);
    const [isTemplatesVisible, setIsTemplatesVisible] = useState(false);

    useEffect(() => {
        dispatch(templateActions.closeCreateModal());
        dispatch(transferActions.setAgendaDrawerVisible(false));
        setIsTemplatesVisible(false);
        return () => {
            dispatch(templateActions.closeCreateModal());
        };
    }, [dispatch]);

    useEffect(() => {
        const pathname = location?.pathname || "";
        if (!/\/v2\/transfer\/[^/]+\/back$/.test(pathname)) {
            return;
        }
        dispatch(transferActions.clearTransferData());
        dispatch(replace(pathname.replace(/\/back$/, "")));
    }, [dispatch, location?.pathname]);

    const handleBack = () => {
        if (embeddedInV3Shell && summary?.returnToPending && isUxDashboardImprovementsEnabled()) {
            dispatch(push(summary.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack("")));
            return;
        }
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            const currentPathname = location?.pathname || "";
            const isExternalOrigin =
                lastHrefPathname?.startsWith("/") &&
                lastHrefPathname !== "/" &&
                !lastHrefPathname.startsWith("/v2/transfer") &&
                lastHrefPathname !== currentPathname;
            if (isExternalOrigin) {
                dispatch(replace(lastHrefPathname));
                return;
            }
        }
        dispatch(goBack());
    };

    const handleCreateTemplateClick = (values) => {
        dispatch(templateActions.createTemplate(values));
    };

    const changeTransferGroup = (newTransferGroup) => {
        dispatch(transferActions.setTransferGroup(newTransferGroup));
        dispatch(transferActions.clearTransferData());
        setAgendaVisible(true);
    };

    const isValidCbu = (value) => {
        const reg = /^\d+$/;
        return reg.test(value) && value.length === parseInt(MAX_REQUIRED, 10);
    };

    if (location?.pathname.startsWith("/v2/transfer/toContact") && transferGroup !== TRANSFER_GROUPS.ToContact) {
        dispatch(transferActions.setTransferGroup(TRANSFER_GROUPS.ToContact));
    }
    if (location?.pathname.startsWith("/v2/transfer/internal") && transferGroup !== TRANSFER_GROUPS.Internal) {
        dispatch(transferActions.setTransferGroup(TRANSFER_GROUPS.Internal));
    }
    if (location?.pathname.startsWith("/v2/transfer/thirdParties") && transferGroup !== TRANSFER_GROUPS.ThirdParties) {
        dispatch(transferActions.setTransferGroup(TRANSFER_GROUPS.ThirdParties));
    }
    let messageId = "transfers.form.thirdParties.title.mb";
    if (location?.pathname === "/v2/transfer/internal") {
        messageId = "transfers.form.internal.title.mb";
    } else if (location?.pathname === "/v2/transfer/toContact") {
        messageId = "transfers.form.toContact.title.mb";
    }

    const v3ShellTitleKey = useMemo(() => {
        if (location?.pathname?.includes("/v2/transfer/internal")) {
            return getTransferInternalTitleI18nKey({ mobile: true });
        }
        if (location?.pathname?.includes("/v2/transfer/toContact")) {
            return "transfers.form.toContact.title.mb";
        }
        return "transfers.form.thirdParties.title.mb";
    }, [location?.pathname]);

    const v3ShellTemplatesButton = useMemo(
        () => (
            <Button
                variant="text"
                icon="NewspaperAlt"
                iconVariant="outline"
                color="white"
                size="large"
                css={{ padding: 8 }}
                onClick={() => {
                    setIsTemplatesVisible(true);
                }}
            />
        ),
        [],
    );

    useEffect(() => {
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatch(layoutActions.setScopes(["form"]));
        if (!isDesktop) {
            dispatch(layoutActions.setTitleKey(v3ShellTitleKey));
            const draftBack =
                summary?.returnToPending &&
                (summary?.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack(""));
            const currentPathname = location?.pathname || "";
            const isExternalOrigin =
                lastHrefPathname?.startsWith("/") &&
                lastHrefPathname !== "/" &&
                !lastHrefPathname.startsWith("/v2/transfer") &&
                lastHrefPathname !== currentPathname;
            const goBackPath =
                draftBack ||
                (isExternalOrigin
                    ? lastHrefPathname
                    : resolveEmbeddedV3GoBackPath(getTransferHistoricPath(), lastHrefPathname));
            dispatch(layoutActions.setGoBackPath(goBackPath));
            dispatch(layoutActions.setRightContentMobile(v3ShellTemplatesButton));
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [
        embeddedInV3Shell,
        isDesktop,
        dispatch,
        v3ShellTitleKey,
        v3ShellTemplatesButton,
        summary?.returnToPending,
        summary?.draftTransactionListGoBackPath,
        lastHrefPathname,
        location?.pathname,
    ]);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="form" />}
            {!isDesktop && !embeddedInV3Shell && (
                <Box className="admin-detail-head z-index-1 px-0">
                    <Head
                        onBack={handleBack}
                        headerClassName="blue-main-header-mobile"
                        centerElement={() => (
                            <Text as="subtitle" className="m-0 no-wrap text-white font-medium">
                                {location?.pathname === "/v2/transfer/internal"
                                    ? getTransferInternalTitleLabel({ mobile: true })
                                    : i18n.get(messageId)}
                            </Text>
                        )}
                        rightContent={() => (
                            <Button
                                variant="solid"
                                icon="NewspaperAlt"
                                iconVariant="outline"
                                className="p-0"
                                size="large"
                                onClick={() => {
                                    setIsTemplatesVisible(true);
                                }}
                            />
                        )}
                        accessibilityTextId="transfers.make.transfer.title"
                    />
                </Box>
            )}
            <Container fixed={!isDesktop} className="h-100">
                {isDesktop && (
                    <Box mx={isDesktop ? 48 : null} className="admin-detail-head px-0">
                        <Head onBack={handleBack} accessibilityTextId="transfers.make.transfer.title" />
                        <Box flex align="center" justify="between" mb={24}>
                            <Text variant="h2" className="font-light font-weight-300">
                                {i18n.get("transfers.make.transfer.title")}
                            </Text>
                            <Button
                                justify="right"
                                text={i18n.get("transfers.templates")}
                                variant="outline"
                                icon="NewspaperAlt"
                                iconVariant="outline"
                                onClick={() => {
                                    setIsTemplatesVisible(true);
                                }}
                            />
                        </Box>
                    </Box>
                )}
                <Grid>
                    <Col size={{ xs: 12, md: 8, lg: 8, xl: 8 }} offset={{ md: 3, lg: 3, xl: 3 }}>
                        {isDesktop ? (
                            <Tabs defaultActiveKey={match.params.transferType || "internal"}>
                                {activeEnvironment.permissions.transferInternal && (
                                    <Tab
                                        eventKey="internal"
                                        label={getTransferInternalTitleLabel()}
                                        onClick={() => {
                                            changeTransferGroup(TRANSFER_GROUPS.Internal);
                                            dispatch(replace("/v2/transfer/internal"));
                                        }}>
                                        {transferGroup === TRANSFER_GROUPS.Internal && (
                                            <TransferInternalForm
                                                isDesktop={isDesktop}
                                                transferGroup={transferGroup}
                                                onCreateTemplate={handleCreateTemplateClick}
                                                agendaVisible={agendaVisible}
                                                setAgendaVisible={setAgendaVisible}
                                                isTemplatesVisible={isTemplatesVisible}
                                                setIsTemplatesVisible={setIsTemplatesVisible}
                                                location={location}
                                            />
                                        )}
                                    </Tab>
                                )}
                                {activeEnvironment.permissions.transferThirdParties && (
                                    <Tab
                                        eventKey="thirdParties"
                                        label={i18n.get("transfers.form.thirdParties.title")}
                                        onClick={() => {
                                            changeTransferGroup(TRANSFER_GROUPS.ThirdParties);
                                            dispatch(replace("/v2/transfer/thirdParties"));
                                        }}>
                                        {transferGroup === TRANSFER_GROUPS.ThirdParties && (
                                            <TransferThirdPartiesForm
                                                isDesktop={isDesktop}
                                                transferGroup={transferGroup}
                                                onCreateTemplate={handleCreateTemplateClick}
                                                isValidCbu={isValidCbu}
                                                agendaVisible={agendaVisible}
                                                setAgendaVisible={setAgendaVisible}
                                                isTemplatesVisible={isTemplatesVisible}
                                                setIsTemplatesVisible={setIsTemplatesVisible}
                                                location={location}
                                            />
                                        )}
                                    </Tab>
                                )}
                                {activeEnvironment.permissions.transferToContact &&
                                    activeEnvironment.permissions.frequentDestination && (
                                        <Tab
                                            eventKey="toContact"
                                            label={i18n.get("transfers.form.contact.title")}
                                            onClick={() => {
                                                changeTransferGroup(TRANSFER_GROUPS.ToContact);
                                                dispatch(replace("/v2/transfer/toContact"));
                                            }}>
                                            {transferGroup === TRANSFER_GROUPS.ToContact && (
                                                <TransferToContactForm
                                                    isDesktop={isDesktop}
                                                    transferGroup={transferGroup}
                                                    onCreateTemplate={handleCreateTemplateClick}
                                                    isValidCbu={isValidCbu}
                                                    schedulerButtonVisible={agendaVisible}
                                                    setSchedulerButtonVisible={setAgendaVisible}
                                                    isTemplatesVisible={isTemplatesVisible}
                                                    setIsTemplatesVisible={setIsTemplatesVisible}
                                                    location={location}
                                                />
                                            )}
                                        </Tab>
                                    )}
                            </Tabs>
                        ) : (
                            <>
                                {activeEnvironment.permissions.transferInternal &&
                                    match.params.transferType === "internal" && (
                                        <TransferInternalForm
                                            isDesktop={isDesktop}
                                            transferGroup={transferGroup}
                                            onCreateTemplate={handleCreateTemplateClick}
                                            agendaVisible={agendaVisible}
                                            setAgendaVisible={setAgendaVisible}
                                            isTemplatesVisible={isTemplatesVisible}
                                            setIsTemplatesVisible={setIsTemplatesVisible}
                                            location={location}
                                        />
                                    )}
                                {activeEnvironment.permissions.transferThirdParties &&
                                    match.params.transferType === "thirdParties" && (
                                        <TransferThirdPartiesForm
                                            isDesktop={isDesktop}
                                            transferGroup={transferGroup}
                                            onCreateTemplate={handleCreateTemplateClick}
                                            isValidCbu={isValidCbu}
                                            agendaVisible={agendaVisible}
                                            setAgendaVisible={setAgendaVisible}
                                            isTemplatesVisible={isTemplatesVisible}
                                            setIsTemplatesVisible={setIsTemplatesVisible}
                                            location={location}
                                        />
                                    )}
                                {activeEnvironment.permissions.transferToContact &&
                                    activeEnvironment.permissions.frequentDestination &&
                                    match.params.transferType === "toContact" && (
                                        <TransferToContactForm
                                            isDesktop={isDesktop}
                                            transferGroup={transferGroup}
                                            onCreateTemplate={handleCreateTemplateClick}
                                            isValidCbu={isValidCbu}
                                            schedulerButtonVisible={agendaVisible}
                                            setSchedulerButtonVisible={setAgendaVisible}
                                            isTemplatesVisible={isTemplatesVisible}
                                            setIsTemplatesVisible={setIsTemplatesVisible}
                                            location={location}
                                        />
                                    )}
                            </>
                        )}
                    </Col>
                </Grid>
            </Container>
        </>
    );
}

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetching(state),
    accounts: transferSelectors.getAccounts(state),
    concepts: transferSelectors.getConcepts(state),
    transferGroup: transferSelectors.getTransferGroup(state),
    summary: transferSelectors.getSummary(state),
    templates: templateSelectors.getTemplateList(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    selectedFromAgenda: transferSelectors.getSelectedFromAgenda(state),
    lastHrefPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(V2Transfer);
